import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import saleService from "../../Services/saleService";
import salesStatsService from "../../Services/salesStatsService";

const Income = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [searchData, setSearchData] = useState("");
  const [medicinesGroupedData, setMedicinesGroupedData] = useState([]);
  const [servicesGroupedData, setServicesGroupedData] = useState([]);
  const [patientGroupedData, setPatientGroupedData] = useState([]);
  const [pharmacyGroupedData, setPharmacyGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [expandedDays, setExpandedDays] = useState({});
  const navigate = useNavigate();

  // ✅ Helpers
  const toDateKey = (dateInput) => {
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const formatDateLabel = (dateInput) => {
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    const options = { year: "numeric", month: "short", day: "numeric" };
    return d.toLocaleDateString("en-US", options);
  };

  const getAllDateKeysInSelectedMonth = (year, month) => {
    const y = Number(year);
    const m0 = Number(month) - 1;
    const lastDay = new Date(y, m0 + 1, 0).getDate();
    const keys = [];
    for (let d = 1; d <= lastDay; d++) {
      keys.push(toDateKey(new Date(y, m0, d)));
    }
    return keys;
  };

  // ✅ Fetching sales data
  useEffect(() => {
  const fetchSalesData = async () => {
    try {
      setLoading(true);

      const [
        patientRes,
        medicineRes,
        servicesRes,
        pharmacyRes,
      ] = await Promise.all([
        salesStatsService.fetchDailyPatientStats(),
        salesStatsService.fetchDailyMedicineStats(),
        salesStatsService.fetchDailyServicesStats(),
        salesStatsService.fetchDailyPharmacyStats(),
      ]);

      setPatientGroupedData(patientRes.daysales || []);
      setMedicinesGroupedData(medicineRes.daymidicinesales || []);
      setServicesGroupedData(servicesRes.dayservicesales || []);
      setPharmacyGroupedData(pharmacyRes.daypharmacysales || []);

      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error("Error fetching statistics");
    }
  };

  fetchSalesData();
}, []);


  // ✅ Grouping functions

  // ✅ Formatting and total helpers
  const fmt = (n) => {
    const v = isNaN(n) ? 0 : Math.round(parseFloat(n) * 100) / 100;
    return v.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const calculateTotal = (data, key) =>
    data.reduce((sum, item) => sum + parseFloat(item[key] || 0), 0);

  // ✅ Prepare combined data
  const allDates = new Set([
  ...patientGroupedData.map((i) => i.date?.split("T")[0]),
  ...medicinesGroupedData.map((i) => i.sale_date?.split("T")[0]),
  ...servicesGroupedData.map((i) => i.sale_date?.split("T")[0]),
  ...pharmacyGroupedData.map((i) => i.sale_date?.split("T")[0]),
]);

const monthDateKeys = Array.from(allDates)
  .filter((date) => {
    const d = new Date(date);
    const m = d.getMonth() + 1;
    const y = d.getFullYear();

    return (
      m === Number(selectedMonth) &&
      y === Number(selectedYear)
    );
  })
  .sort();
  

  const medicinesMap = Object.fromEntries(
  medicinesGroupedData.map((item) => [
    item.sale_date?.split("T")[0],
    item,
  ])
);

const servicesMap = Object.fromEntries(
  servicesGroupedData.map((item) => [
    item.sale_date?.split("T")[0],
    item,
  ])
);

const patientsMap = Object.fromEntries(
  patientGroupedData.map((item) => [
    item.date?.split("T")[0],
    item,
  ])
);

const pharmacyMap = Object.fromEntries(
  pharmacyGroupedData.map((item) => [
    item.sale_date?.split("T")[0],
    item,
  ])
);

const combinedData = monthDateKeys.map((dateKey) => {
  const patient = patientsMap[dateKey] || {};
  const medicine = medicinesMap[dateKey] || {};
  const service = servicesMap[dateKey] || {};
  const pharmacy = pharmacyMap[dateKey] || {};

  return {
    dateKey,
    date: formatDateLabel(new Date(dateKey)),

    patientSales: Number(patient.total_patients || 0),
    patientAmount: Number(patient.total_fee || 0),

    medicinesSales: Number(medicine.total_sales || 0),
    medicinesAmount: Number(medicine.total_amount || 0),

    servicesSales: Number(service.total_sales || 0),
    servicesAmount: Number(service.total_amount || 0),

    pharmacySales: Number(pharmacy.total_sales || 0),
    pharmacyAmount: Number(pharmacy.total_amount || 0),
  };
});

  const grandTotal =
    calculateTotal(combinedData, "patientAmount") +
    calculateTotal(combinedData, "medicinesAmount") +
    calculateTotal(combinedData, "servicesAmount") +
    calculateTotal(combinedData, "pharmacyAmount");

  const categories = [
    { label: "Patient", keyAmount: "patientAmount", keySales: "patientSales" },
    {
      label: "Medicines",
      keyAmount: "medicinesAmount",
      keySales: "medicinesSales",
    },
    {
      label: "Services",
      keyAmount: "servicesAmount",
      keySales: "servicesSales",
    },
    {
      label: "Pharmacy",
      keyAmount: "pharmacyAmount",
      keySales: "pharmacySales",
    },
  ];

  // ✅ Toggle mobile card expand/collapse
  const toggleDay = (dateKey) => {
    setExpandedDays((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  // ✅ Column widths for desktop table
  const colWidths = [
    "110px",
    "48px",
    "90px",
    "48px",
    "90px",
    "48px",
    "90px",
    "48px",
    "90px",
    "95px",
  ];
  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w, minWidth: w }} />
      ))}
    </colgroup>
  );

  return (
    <div className="p-3 sm:p-4 font-sans">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
          Income Summary
        </p>
        <div className="flex gap-2">
          <select
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((name, i) => (
              <option key={i} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {[2024, 2025, 2026].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-4">
        {categories.map(({ label, keyAmount, keySales }) => (
          <div
            key={label}
            className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5"
          >
            <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
              {label}
            </p>
            <p className="text-base font-semibold text-gray-800">
              {fmt(calculateTotal(combinedData, keyAmount))}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {calculateTotal(combinedData, keySales)} sales
            </p>
          </div>
        ))}
        <div className="bg-gray-100 rounded-lg px-3 py-2.5 border-t-2 border-[#F07F1C] col-span-2 sm:col-span-3 lg:col-span-1">
          <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
            Grand Total
          </p>
          <p className="text-base font-bold text-[#F07F1C]">
            {fmt(grandTotal)}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {combinedData.length} days
          </p>
        </div>
      </div>

      {/* ══════════════════════════════════════
          MOBILE VIEW — Collapsible Day Cards
          (shown on screens < 640px)
      ══════════════════════════════════════ */}
      <div className="block sm:hidden">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            Loading...
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {combinedData.map((item) => {
                const rowTotal =
                  parseFloat(item.patientAmount) +
                  parseFloat(item.medicinesAmount) +
                  parseFloat(item.servicesAmount) +
                  parseFloat(item.pharmacyAmount);

                const isOpen = !!expandedDays[item.dateKey];
                const hasData = rowTotal > 0;

                return (
                  <div
                    key={item.dateKey}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    {/* Card Header — always visible */}
                    <button
                      onClick={() => toggleDay(item.dateKey)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 text-left"
                    >
                      <span className="text-[12px] font-medium text-gray-700">
                        {item.date}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[13px] font-semibold ${
                            hasData ? "text-[#F07F1C]" : "text-gray-300"
                          }`}
                        >
                          {fmt(rowTotal)}
                        </span>
                        <svg
                          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {/* Card Body — expanded breakdown */}
                    {isOpen && (
                      <div className="px-3 py-2 bg-white divide-y divide-gray-100">
                        {categories.map(({ label, keyAmount, keySales }) => (
                          <div
                            key={label}
                            className="flex items-center justify-between py-2"
                          >
                            <span className="text-[12px] text-gray-500 w-20">
                              {label}
                            </span>
                            <div className="flex items-center gap-4">
                              <span className="text-[11px] text-gray-400 w-12 text-right">
                                {item[keySales]} sales
                              </span>
                              <span className="text-[12px] font-medium text-gray-800 w-20 text-right">
                                {fmt(item[keyAmount])}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile Grand Total Footer */}
            <div className="mt-3 bg-[#1F2937] rounded-xl px-3 py-3">
              {categories.map(({ label, keyAmount, keySales }) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-1.5 border-b border-white/10 last:border-0"
                >
                  <span className="text-[12px] text-gray-400">{label}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-[11px] text-gray-500 w-14 text-right">
                      {calculateTotal(combinedData, keySales) | 0} sales
                    </span>
                    <span className="text-[12px] text-gray-100 font-medium w-20 text-right">
                      {fmt(calculateTotal(combinedData, keyAmount))}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/20">
                <span className="text-[12px] font-medium text-gray-100">
                  Grand Total
                </span>
                <span className="text-[14px] font-bold text-[#F07F1C]">
                  {fmt(grandTotal)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════
          DESKTOP VIEW — Single table, sticky thead + tfoot
          (shown on screens >= 640px)
      ══════════════════════════════════════ */}
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <ColGroup />

            {/* STICKY HEADER */}
            <thead>
              <tr>
                <th className="sticky top-0 z-20 bg-[#1F2937] text-left px-3 py-2.5 text-[11px] text-gray-100 uppercase border-r border-white/10">
                  Date
                </th>
                {["Patient", "Medicines", "Services", "Pharmacy"].map((col) => (
                  <th
                    key={col}
                    colSpan={2}
                    className="sticky top-0 z-20 bg-[#1F2937] text-center px-2 py-2.5 text-[11px] text-gray-100 uppercase border-r border-white/10"
                  >
                    {col}
                  </th>
                ))}
                <th className="sticky top-0 z-20 bg-[#1F2937] text-right px-3 py-2.5 text-[11px] text-gray-100 uppercase">
                  Total
                </th>
              </tr>
              <tr>
                <th className="sticky top-[37px] z-20 bg-gray-100 border-b border-r border-gray-200 py-1.5"></th>
                {["Patient", "Medicines", "Services", "Pharmacy"].flatMap(
                  (col) => [
                    <th
                      key={`${col}-s`}
                      className="sticky top-[37px] z-20 bg-gray-100 text-gray-500 text-center px-2 py-1.5 text-[11px] border-b border-r border-gray-200 font-normal"
                    >
                      Sales
                    </th>,
                    <th
                      key={`${col}-a`}
                      className="sticky top-[37px] z-20 bg-gray-100 text-gray-500 text-center px-2 py-1.5 text-[11px] border-b border-r border-gray-200 font-normal"
                    >
                      Amount
                    </th>,
                  ],
                )}
                <th className="sticky top-[37px] z-20 bg-gray-100 border-b border-gray-200 py-1.5"></th>
              </tr>
            </thead>

            {/* BODY */}
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : (
                combinedData.map((item, index) => {
                  const rowTotal =
                    parseFloat(item.patientAmount) +
                    parseFloat(item.medicinesAmount) +
                    parseFloat(item.servicesAmount) +
                    parseFloat(item.pharmacyAmount);
                  return (
                    <tr
                      key={item.dateKey}
                      className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${
                        index % 2 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <td className="px-3 py-1.5 text-gray-400 text-[12px] border-r border-gray-100">
                        {item.date}
                      </td>
                      <td className="px-2 py-1.5 text-center border-r border-gray-100 text-[12px]">
                        {item.patientSales}
                      </td>
                      <td className="px-2 py-1.5 text-right border-r border-gray-100 text-[12px]">
                        {fmt(item.patientAmount)}
                      </td>
                      <td className="px-2 py-1.5 text-center border-r border-gray-100 text-[12px]">
                        {item.medicinesSales}
                      </td>
                      <td className="px-2 py-1.5 text-right border-r border-gray-100 text-[12px]">
                        {fmt(item.medicinesAmount)}
                      </td>
                      <td className="px-2 py-1.5 text-center border-r border-gray-100 text-[12px]">
                        {item.servicesSales}
                      </td>
                      <td className="px-2 py-1.5 text-right border-r border-gray-100 text-[12px]">
                        {fmt(item.servicesAmount)}
                      </td>
                      <td className="px-2 py-1.5 text-center border-r border-gray-100 text-[12px]">
                        {item.pharmacySales}
                      </td>
                      <td className="px-2 py-1.5 text-right border-r border-gray-100 text-[12px]">
                        {fmt(item.pharmacyAmount)}
                      </td>
                      <td className="px-3 py-1.5 text-right text-[12px] font-medium">
                        {fmt(rowTotal)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* STICKY FOOTER */}
            <tfoot>
              <tr className="bg-[#1F2937] text-gray-100">
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-3 py-2.5 font-medium text-[12px] border-r border-white/10 border-t ">
                  Grand Total
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-center text-[12px] border-r border-white/10 border-t ">
                  {calculateTotal(combinedData, "patientSales")}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-right text-[12px] border-r border-white/10 border-t">
                  {fmt(calculateTotal(combinedData, "patientAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-center text-[12px] border-r border-white/10 border-t">
                  {calculateTotal(combinedData, "medicinesSales")}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-right text-[12px] border-r border-white/10 border-t">
                  {fmt(calculateTotal(combinedData, "medicinesAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-center text-[12px] border-r border-white/10 border-t">
                  {calculateTotal(combinedData, "servicesSales")}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-right text-[12px] border-r border-white/10 border-t">
                  {fmt(calculateTotal(combinedData, "servicesAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-center text-[12px] border-r border-white/10 border-t">
                  {calculateTotal(combinedData, "pharmacySales")}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-2 py-2.5 text-right text-[12px] border-r border-white/10 border-t">
                  {fmt(calculateTotal(combinedData, "pharmacyAmount"))}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-3 py-2.5 text-right text-[12px] font-semibold border-t border-white/10">
                  {fmt(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Income;

